/**
 * Turning what a person types into what the API stores, and back (VRG-459).
 *
 * ## THE WHOLE POINT OF THIS FILE IS THE UNIT, AND IT IS NOT A VALIDATION PROBLEM
 *
 * `METRIC_INPUT_SPECS` stores rates 0–1 and currency in MINOR units. A form that
 * asked for those directly would be asking a human to type `0.62` for 62% and
 * `25000000` for $250,000 — and the second is the 100x error this codebase has
 * already paid for once. `250000` typed into a cents field is a VALID number:
 * `metricInputValueError` accepts it, the API accepts it, and the ROI headline is
 * wrong by two orders of magnitude with nothing at the scene to say so.
 *
 * So the field speaks in the units a person thinks in — dollars and percent — and
 * the conversion happens HERE, in one place, on both sides. Three defences, in
 * increasing order of how much they actually catch:
 *
 *   1. `adornment`  — the unit is IN the field, not only in the label.
 *   2. `display`    — the parsed value echoed under the input AS IT IS TYPED, so
 *                     `250000` immediately reads back `$250,000.00`.
 *   3. `magnitude`  — the same number IN WORDS. "$250,000.00" and "$2,500.00" are
 *                     three glyphs apart and easy to skim past; "two hundred fifty
 *                     thousand dollars" and "two thousand five hundred dollars" are
 *                     not. This is the one that catches a factor-of-100 slip, and
 *                     it costs nothing.
 *
 * None of the three is a validator, deliberately. No arithmetic check distinguishes
 * a units error from a large company, so the defence has to be a HUMAN reading an
 * absurd output — which means the output has to be legible enough to be absurd.
 */
import {
  DEFAULT_REPORTING_CURRENCY,
  METRIC_BY_ID,
  METRIC_INPUT_SPECS,
  metricInputValueError,
  type IMetricInputDefinition,
  type IMetricInputValue,
  type MetricInputKey,
  type MetricRole,
  type MetricSolution,
} from '@repo/types'

import { formatMoneyCents, moneySymbol } from '../analyticsMoney'

/**
 * ── THE CURRENCY OF A BRAND'S OWN BUSINESS FIGURES (VRG-497) ────────────────────────
 *
 * This was `METRIC_INPUT_CURRENCY = 'USD'`, a hardcoded constant whose own docblock
 * recorded the deferral and the exact shape of the fix. It is now wired, and the wiring
 * is THREE separable rules that must not be collapsed into one:
 *
 *   READ   — a stored amount is formatted in ITS OWN `IMetricInputValue.currency`,
 *            never in the company's. Sourcing the label from the company setting would
 *            relabel a stored 50000 as `£500.00` the moment a brand switched to GBP,
 *            with the stored amount unchanged — restating one currency's number as
 *            another's, which is precisely the read-time-relabelling error
 *            `reporting_currency` was introduced to prevent (migration 0096's header).
 *   WRITE  — a NEW entry defaults to the company's `reporting_currency`, and the field's
 *            `$` adornment follows it. That currency arrives on the metric-inputs
 *            response (`reporting_currency`), so all three surfaces that render this
 *            form read one value.
 *   STALE  — an amount stored in something else is KEPT and FLAGGED, never converted and
 *            never force-re-entered. Forcing re-entry punishes the brand for switching
 *            and risks losing a figure nobody has to hand; converting makes every
 *            historical number drift as rates move. So it stays, renders in its own
 *            currency, and says it is not counting — see `metricInputStale`.
 *
 * `DEFAULT_REPORTING_CURRENCY` is the fallback for a caller that has not got the
 * company's answer yet (the first frames of a load). It is NOT a default to lean on: an
 * amount whose currency is genuinely unknown is a stored row, and stored rows carry
 * their own.
 */
export const metricInputCurrency = (
  value: Pick<IMetricInputValue, 'currency'> | null | undefined,
  reportingCurrency: string = DEFAULT_REPORTING_CURRENCY,
): string => (value?.currency || reportingCurrency).toUpperCase()

/**
 * Is this stored figure denominated in something the company no longer reports in?
 *
 * ⚠️ NULL/ABSENT IS NOT STALE. A rate carries no currency by design
 * (`prepareMetricInputs` nulls it so nobody reads the number as an amount), and a
 * historical amount may predate the column. Flagging either would put a warning on
 * every figure of a brand that switched currency, including the ones that are fine —
 * and a caveat that is usually wrong teaches a reader to stop reading caveats.
 */
export const metricInputStale = (
  value: Pick<IMetricInputValue, 'currency'> | null | undefined,
  reportingCurrency: string | null | undefined,
): boolean => {
  if (!value?.currency || !reportingCurrency) return false
  return value.currency.toUpperCase() !== reportingCurrency.toUpperCase()
}

/** What the user types in, as distinct from what is stored. */
export type MetricInputEntryUnit = 'dollars' | 'percent'

export const entryUnitOf = (
  spec: Pick<IMetricInputDefinition, 'unit'>,
): MetricInputEntryUnit => (spec.unit === 'rate' ? 'percent' : 'dollars')

/**
 * The unit marker rendered INSIDE the field.
 *
 * `$` leads (it precedes the digits in the written form) and `%` trails, matching
 * how each is written rather than picking one side for both.
 *
 * ⚠️ THE SYMBOL IS A CLAIM ABOUT THE NUMBER IN THE BOX, so the caller must pass the
 * currency THAT number is in — a stored value's own, or the reporting currency for an
 * empty field (VRG-497). It is the first of this file's three defences against a units
 * error, and a defence that shows the wrong unit is worse than none: `$250,000` beside
 * a figure a brand entered in pounds reads as verified rather than as unchecked.
 * Derived through `moneySymbol` rather than a literal, so a zero-decimal or
 * symbol-less currency renders whatever `Intl` says it should.
 */
export const unitAdornment = (
  spec: Pick<IMetricInputDefinition, 'unit'>,
  currency: string = DEFAULT_REPORTING_CURRENCY,
): { start?: string; end?: string } =>
  spec.unit === 'rate' ? { end: '%' } : { start: moneySymbol(currency) }

/**
 * `Intl` is not free to construct and these run per keystroke, so instances are
 * memoised per currency — the same reasoning (and the same shape) as
 * `analyticsMoney`'s own cache.
 *
 * ⚠️ TWO FIXED DECIMALS, unlike `analyticsMoney.formatMoneyCents`, which asks `Intl`
 * for the currency's own exponent. That difference is deliberate and narrow: this
 * formatter renders what a person TYPED into a two-decimal money field, so `250000`
 * must echo `$2,500.00` and not `$250,000`. A zero-decimal currency (JPY, KRW) would
 * be shown a fractional part it does not use — a real, known cost, and a smaller one
 * than an echo that silently rescales the number it is checking.
 */
const entryFormatterCache = new Map<string, Intl.NumberFormat>()
const entryFormatter = (currency: string): Intl.NumberFormat => {
  const key = currency.toUpperCase()
  const hit = entryFormatterCache.get(key)
  if (hit) return hit

  let made: Intl.NumberFormat
  try {
    made = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: key,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  } catch {
    // An unrecognised code makes `Intl` THROW, during render. The API validates
    // against an allowlist and the DB CHECKs the shape, so this should be
    // unreachable; it is caught anyway because a blank settings page is far worse
    // than a wrong symbol. Same guard, same reasoning, as `analyticsMoney`.
    made = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: DEFAULT_REPORTING_CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  entryFormatterCache.set(key, made)
  return made
}

/** A stored value rendered the way the field asks for it. `0.62` → `"62"`. */
export const storedToEntry = (
  spec: Pick<IMetricInputDefinition, 'unit'>,
  stored: number,
): string => {
  if (spec.unit === 'rate') {
    // `Number(...)` strips the trailing zeros `toFixed` adds, so 0.62 reads "62"
    // and not "62.0000". Four places because a rate is stored 0–1 and two would
    // silently round 0.0625 (6.25%) to 6.3%.
    return String(Number((stored * 100).toFixed(4)))
  }
  return String(Number((stored / 100).toFixed(2)))
}

/**
 * The same entry string with thousands separators, for a field that is NOT focused.
 *
 * ## Why this exists at all, and what it replaced
 *
 * The parsed echo under the field used to render at rest, so a saved figure printed
 * `$500.00` beneath an input already reading `500` beside a `$`, above a "Set in Vera
 * on ..." line — one fact, three times. The echo's job is catching a units error IN
 * FLIGHT, so it is now dirty-only (see `MetricInputField`), and this is what covers the
 * legibility the echo was incidentally providing at rest.
 *
 * The gap is real and it is only real at scale: `2500000` is not readable as two and a
 * half million without grouping, on a page whose entire purpose is auditing the numbers
 * a dashboard rests on. Fixing that in the input the number already occupies is
 * strictly better than a second line restating it — the second line was the clutter.
 *
 * ## ⚠️ DISPLAY ONLY. It must never reach the draft
 *
 * The caller swaps this in only while the field is blurred and swaps the raw draft back
 * on focus. Writing a grouped string INTO the draft would break two things at once:
 * `isDirty` compares the draft against the saved entry, so grouping it would mark an
 * untouched field dirty and light up Save; and re-grouping between keystrokes moves the
 * caret, which is the classic money-field bug. Grouping only a blurred value needs
 * neither defence. (`parseMetricInput` strips commas regardless, so a grouped value
 * that somehow did reach it still parses.)
 *
 * Rates are returned untouched — bounded 0–100 by definition, so there is nothing to
 * group and nothing to misread.
 */
export const groupEntryForDisplay = (
  spec: Pick<IMetricInputDefinition, 'unit'>,
  entry: string,
): string => {
  if (spec.unit === 'rate') return entry

  const cleaned = entry.trim().replace(/,/g, '')
  // Anything that is not a plain unsigned decimal is handed back verbatim: a
  // half-typed or malformed value has to stay exactly as the reader left it, because
  // the error message beneath refers to what they wrote.
  const match = /^(\d+)(\.\d*)?$/.exec(cleaned)
  if (!match) return entry

  const [, whole, fraction = ''] = match
  // A regex rather than `Number(...).toLocaleString()`: the whole part is arbitrary
  // user text, and routing it through a float would silently round a digit string past
  // 2^53 — a units error being *introduced* by the thing that displays it.
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction}`
}

/**
 * The three states a figure can be in, as ONE function both the card and the page
 * header read.
 *
 * ## The counts have to agree, and a comment saying so is not a mechanism
 *
 * The page header reads "N of M supplied · K unconfirmed" and each card carries a
 * marker. If the two were derived independently, a reader counting markers could get a
 * different answer from the header sitting above them — which on an auditing surface is
 * worse than having neither. So both are computed from this:
 *
 *     marker present (either colour)   ⇔  counts toward "supplied"
 *     marker is the amber one           ⇔  counts toward "unconfirmed"
 *
 * That mapping is exactly how the header sentence reads: "· K unconfirmed" QUALIFIES
 * the supplied count rather than naming a separate bucket. A provisional figure is
 * supplied — it is already driving the metrics — and it is also the thing still wanting
 * attention, so it appears in both terms and carries one marker that says both.
 *
 * ⚠️ This also fixed a real disagreement: the header used to count provisionals across
 * every STORED value while counting "supplied" across the CATALOGUE. A stored value
 * whose key has left the catalogue has no card, so it could be counted as unconfirmed
 * with nothing on the page to confirm.
 */
export type MetricInputMarkerState = 'unset' | 'unconfirmed' | 'confirmed'

export const metricInputMarkerState = (
  value: IMetricInputValue | null | undefined,
): MetricInputMarkerState => {
  if (!value) return 'unset'
  return value.provisional ? 'unconfirmed' : 'confirmed'
}

/**
 * The state of a whole METRIC's assumption, collapsed from the states of the inputs
 * it declares — what the card's marker beside the figure colours itself from.
 *
 * Built on `metricInputMarkerState` rather than re-testing `provisional` here, and
 * that is the point of it existing at all: the settings page and the card would
 * otherwise each own a copy of "what counts as unconfirmed", and the day one changed
 * a reader could get two different answers about the same figure on two surfaces.
 * §44's argument for one definition, applied to a classifier rather than to a word.
 *
 * ## Why an `unset` input does NOT make it amber
 *
 * Amber is the "still wants attention FROM YOU, on a figure already driving this
 * number" signal. An unset input is not that — it is a figure that is not driving
 * anything yet, and the card renders `N inputs needed` in the figure's own slot for
 * exactly that case, so a second amber signal beside it would be describing a state
 * the tile is already fully occupied by saying. Only `unconfirmed` escalates.
 *
 * ## Empty inputs → `confirmed`, and that is the deliberate LOADING answer too
 *
 * The catalogue is fetched, so it is empty for the first frames of a page load. That
 * makes this return `confirmed` — the neutral marker — before the data lands, and
 * neutral is the right thing to be wrong with: the marker's base claim ("this rests
 * on a figure you supplied") is TRUE in both states, and amber is the ADDITION. So a
 * late arrival escalates a true-but-quiet marker into a louder one, rather than
 * flashing a warning at a reader and then withdrawing it.
 */
export const metricAssumptionState = (
  catalogue: IMetricInputDefinition[],
  values: IMetricInputValue[],
  metricId: string,
): Extract<MetricInputMarkerState, 'unconfirmed' | 'confirmed'> =>
  catalogue
    .filter(d => d.metric_ids.includes(metricId))
    .some(d => metricInputMarkerState(valueForKey(values, d.key)) === 'unconfirmed')
    ? 'unconfirmed'
    : 'confirmed'

/**
 * A stored value rendered for READING — the provenance line, a summary, a table.
 *
 * VRG-497 — `currency` is the currency THIS amount is stored in
 * (`IMetricInputValue.currency`), not the company's. See this file's header for why the
 * two must not be conflated.
 */
export const formatStoredValue = (
  spec: Pick<IMetricInputDefinition, 'unit'>,
  stored: number,
  currency: string = DEFAULT_REPORTING_CURRENCY,
): string =>
  spec.unit === 'rate'
    ? `${Number((stored * 100).toFixed(4))}%`
    : entryFormatter(currency).format(stored / 100)

/**
 * Cents → `"$1,234.00"` / `"£1,234.00"`. The one currency formatter, so nothing
 * re-rolls one.
 *
 * VERA-260 — `currency` is now a parameter. It defaults to `METRIC_INPUT_CURRENCY`
 * so every existing call site keeps its exact behaviour, but an ROI figure MUST pass
 * `IRoiMetrics.reporting_currency`: the money aggregates include only rows already
 * priced in it (foreign-currency rows are excluded and counted, never converted), so
 * the symbol is a fact about which rows the figure contains rather than a display
 * preference. Delegates to `analyticsMoney` rather than holding its own `Intl`
 * instance — one currency formatter for the surface, not two that agree until they
 * do not.
 */
export const formatCents = (cents: number, currency?: string): string =>
  formatMoneyCents(cents, currency ?? DEFAULT_REPORTING_CURRENCY)

const SMALL_WORDS = [
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
  'seventeen', 'eighteen', 'nineteen',
]
const TENS_WORDS = [
  '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety',
]
const SCALE_WORDS: Array<[number, string]> = [
  [1_000_000_000, 'billion'],
  [1_000_000, 'million'],
  [1_000, 'thousand'],
]

/**
 * A whole number in words, for the magnitude echo.
 *
 * Handles up to the low billions and gives up above that with a plain "over a
 * billion" rather than producing a sentence nobody can parse — the point is a
 * magnitude a reader recognises, not a cheque.
 */
const wholeNumberInWords = (n: number): string => {
  if (!Number.isFinite(n) || n < 0) return ''
  if (n >= 1_000_000_000_000) return 'over a trillion'
  if (n < 20) return SMALL_WORDS[n]
  if (n < 100) {
    const tens = TENS_WORDS[Math.floor(n / 10)]
    const ones = n % 10
    return ones ? `${tens}-${SMALL_WORDS[ones]}` : tens
  }
  if (n < 1000) {
    const hundreds = `${SMALL_WORDS[Math.floor(n / 100)]} hundred`
    const rest = n % 100
    return rest ? `${hundreds} ${wholeNumberInWords(rest)}` : hundreds
  }
  for (const [scale, word] of SCALE_WORDS) {
    if (n >= scale) {
      const lead = Math.floor(n / scale)
      const rest = n % scale
      const head = `${wholeNumberInWords(lead)} ${word}`
      return rest ? `${head} ${wholeNumberInWords(rest)}` : head
    }
  }
  return String(n)
}

/**
 * The magnitude echo — the same amount in words.
 *
 * Only for currency. A percentage has no magnitude ambiguity to resolve: it is
 * bounded 0–100 by its own definition, so there is no 100x slip available and
 * "sixty-two percent" beside "62%" is noise.
 */
export const magnitudeInWords = (
  spec: Pick<IMetricInputDefinition, 'unit'>,
  storedValue: number,
  currency: string = DEFAULT_REPORTING_CURRENCY,
): string | null => {
  if (spec.unit !== 'currency_minor') return null

  const majorUnits = Math.round(storedValue / 100)
  // Under a thousand there is nothing to mistake — "four hundred dollars" adds
  // no information a reader did not already get from "$400.00".
  if (majorUnits < 1000) return null

  /*
    VRG-497 — the noun follows the currency. It said "dollars" unconditionally, which
    on a GBP field turned the ONE defence that actually catches a 100x slip into a
    sentence contradicting the symbol six pixels to its left. A reader who has to
    reconcile "£" against "dollars" is not reading the magnitude, which is the only
    thing this line is for.

    `Intl.DisplayNames` rather than a hand-written code→noun map: a second map is a
    second thing to update when the currency catalogue grows, and it disagrees with
    `moneySymbol` the first time somebody adds a code to one and not the other (the
    argument `moneySymbol`'s own docblock makes). It yields "British Pounds" for GBP,
    so it is lowercased to sit in running prose. Where a runtime has no display name it
    returns the CODE, which is the correct answer — "nine hundred thousand GBP" is
    unambiguous — rather than a fallback.
  */
  return `${wholeNumberInWords(majorUnits)} ${currencyNoun(currency)}`
}

const currencyNounCache = new Map<string, string>()
const currencyNoun = (currency: string): string => {
  const key = currency.toUpperCase()
  const hit = currencyNounCache.get(key)
  if (hit) return hit

  let noun = key
  try {
    const display = new Intl.DisplayNames(undefined, {
      type: 'currency',
    }).of(key)
    if (display && display !== key) noun = display.toLowerCase()
  } catch {
    // `Intl.DisplayNames` is absent on older runtimes and throws on a malformed code.
    // The code itself is a true, readable answer, so there is nothing to recover.
  }

  currencyNounCache.set(key, noun)
  return noun
}

export type ParsedMetricInput = {
  /** Empty input — not an error, just nothing to save yet. */
  empty: boolean
  /** The value in the API's units: 0–1 for a rate, minor units for currency. */
  storedValue: number | null
  /** The parsed amount echoed back: `"$250,000.00"`, `"62%"`. */
  display: string | null
  /** The same amount in words, for currency only. See `magnitudeInWords`. */
  magnitude: string | null
  /**
   * Customer-facing reason the value cannot be saved, or null.
   *
   * Table stakes only — type and range. It catches a negative acquisition cost and
   * a 350% margin, and it CANNOT catch the failure that matters (see this file's
   * header). Treat null as "not obviously junk", never as "correct".
   */
  error: string | null
}

/**
 * The shared validator's reason, restated in the units the FIELD asked in.
 *
 * `entered` is what the person typed (percent, or dollars) — not the stored value —
 * because the whole point is to describe the bound where they can act on it. The
 * bounds come from `METRIC_INPUT_SPECS` and are CONVERTED here rather than retyped,
 * so widening a spec moves the sentence with it.
 *
 * Falls back to the shared message for any reason this does not recognise. That is
 * deliberate: a new failure mode should surface imperfectly worded rather than
 * silently as "valid", and the fallback can only ever be too technical, never wrong.
 */
const entryUnitError = (
  spec: IMetricInputDefinition,
  entered: number,
  fallback: string,
  currency: string,
): string => {
  if (spec.unit === 'rate') {
    const minPct = spec.min * 100
    const maxPct = spec.max === null ? null : spec.max * 100
    if (entered < minPct) return `${spec.label} cannot be below ${minPct}%.`
    if (maxPct !== null && entered > maxPct) {
      return `${spec.label} is a percentage between ${minPct} and ${maxPct}.`
    }
    return fallback
  }

  const minMajor = spec.min / 100
  const maxMajor = spec.max === null ? null : spec.max / 100
  if (entered < minMajor) {
    return minMajor === 0
      ? `${spec.label} cannot be negative.`
      : `${spec.label} cannot be below ${entryFormatter(currency).format(minMajor)}.`
  }
  if (maxMajor !== null && entered > maxMajor) {
    return `${spec.label} cannot be above ${entryFormatter(currency).format(maxMajor)}.`
  }
  return fallback
}

const EMPTY_PARSE: ParsedMetricInput = {
  empty: true,
  storedValue: null,
  display: null,
  magnitude: null,
  error: null,
}

/**
 * Parse one field's raw text into the value the API stores.
 *
 * Commas, spaces, a leading `$` and a trailing `%` are all stripped before parsing:
 * people paste `$250,000` out of a spreadsheet, and rejecting that as "not a
 * number" teaches them the field is fussy rather than that the value is wrong.
 */
export const parseMetricInput = (
  spec: IMetricInputDefinition,
  raw: string,
  /**
   * VRG-497 — the currency this field is ASKING IN: a stored value's own, or the
   * company's reporting currency for an empty field. It reaches the two echoes
   * (`display`, `magnitude`) and the bound messages, all three of which are statements
   * about the number the reader is looking at.
   */
  currency: string = DEFAULT_REPORTING_CURRENCY,
): ParsedMetricInput => {
  /*
    The symbol is stripped along with the digits' separators. `$` and `%` are both
    listed, plus the CURRENT currency's own symbol, because people paste `£250,000` out
    of a spreadsheet and rejecting that as "not a number" teaches them the field is
    fussy rather than that the value is wrong. `$` stays in the set unconditionally
    (rather than only when it IS the symbol) for the same forgiving reason — a stray
    `$` in a GBP field is a paste artefact, not a claim about currency, and the field
    already states its own unit.

    Escaped, because a symbol can be a regex metacharacter (`$` most obviously) and an
    unescaped one silently changes what the pattern matches.
  */
  const symbol = moneySymbol(currency)
  const strip = new RegExp(
    `[,\\s$%${symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`,
    'g',
  )
  const cleaned = raw.replace(strip, '')
  if (cleaned === '') return EMPTY_PARSE

  const entered = Number(cleaned)
  if (!Number.isFinite(entered)) {
    return { ...EMPTY_PARSE, empty: false, error: 'Enter a number.' }
  }

  // The conversion into stored units. `Math.round` on the currency branch is not
  // cosmetic: floating-point makes `2500.07 * 100` land on 250006.99999999997, and
  // the API rejects a non-integral minor-unit amount outright.
  const storedValue =
    spec.unit === 'rate' ? entered / 100 : Math.round(entered * 100)

  /*
    ── VALIDITY FROM THE SHARED VALIDATOR, WORDING FROM HERE ──────────────────────

    `metricInputValueError` is the authority on WHETHER the value is acceptable, and
    it must stay so: the server runs this exact function on this exact number, so a
    field that shows no error cannot be one the API then rejects. Re-implementing its
    bounds locally would let the two drift and produce precisely that.

    ⚠️ ITS MESSAGE, THOUGH, IS IN STORAGE UNITS, AND THIS FIELD IS NOT. Typing 350
    into a field labelled with a `%` returned "Gross margin is a rate between 0 and 1,
    not a percentage" — advice that is correct about `IMetricInputValue` and, followed
    literally in this field, would enter 0.035%. Caught in the browser; it is the
    mirror image of the units error this whole module exists to prevent, so surfacing
    it verbatim would have the field arguing for the mistake.

    So the reason is restated in the units the field ASKED in. The bounds themselves
    are still the spec's — converted, not retyped — so a change to `METRIC_INPUT_SPECS`
    moves both the check and the sentence.
  */
  const rawError = metricInputValueError(spec.key, storedValue)
  const error =
    rawError === null
      ? null
      : entryUnitError(spec, entered, rawError, currency)

  return {
    empty: false,
    storedValue,
    display: formatStoredValue(spec, storedValue, currency),
    magnitude: magnitudeInWords(spec, storedValue, currency),
    error,
  }
}

/**
 * An input's roles and solutions — the UNION over every metric it feeds.
 *
 * ## ⚠️ INPUTS DO NOT CARRY THEIR OWN TAXONOMY, and the next reader will assume they do
 *
 * `roles` and `solutions` are declared on the METRIC (`IAnalyticsMetric`), and one input
 * feeds several metrics — that is the whole reason the storage is keyed by input rather
 * than by metric. So an input's roles are whichever roles care about ANY metric it
 * feeds, computed here from `METRIC_BY_ID`.
 *
 * Authoring a second `input -> roles` table would be quicker to read and is exactly the
 * duplication §36 and §44 both exist to stop: the day someone retags a metric, the
 * filter on this page would silently disagree with the analytics tab that shows it, and
 * nothing compares the two.
 *
 * `vera_spend`'s metric declares `solutions: []` deliberately — Vera's own cost is not a
 * customer-facing solution area — so an input feeding only that metric yields an EMPTY
 * solution set. Callers must treat "no solutions" as "matches no solution filter" rather
 * than as missing data; it is a true statement about the metric, not a gap.
 */
export const metricInputTaxonomy = (
  definition: Pick<IMetricInputDefinition, 'metric_ids'>,
): { roles: MetricRole[]; solutions: MetricSolution[] } => {
  const roles = new Set<MetricRole>()
  const solutions = new Set<MetricSolution>()
  for (const id of definition.metric_ids) {
    const metric = METRIC_BY_ID[id]
    if (!metric) continue
    metric.roles?.forEach(r => roles.add(r))
    metric.solutions?.forEach(s => solutions.add(s))
  }
  return { roles: [...roles], solutions: [...solutions] }
}

/** This company's value for one input, or null. */
export const valueForKey = (
  values: IMetricInputValue[],
  key: MetricInputKey,
): IMetricInputValue | null => values.find(v => v.key === key) ?? null

/**
 * Where a value came from, as a line under the field.
 *
 * `provisional` is the load-bearing half and it is stated FIRST, because it is the
 * only one that changes what the reader should do. A signup-supplied figure has
 * never been confirmed by anyone in-app, and the epic's locked decision is that
 * such a number must not silently drive a headline — so it says "unconfirmed" in
 * the visible line rather than carrying a subtler badge.
 */
export const describeProvenance = (value: IMetricInputValue): string => {
  const when = value.set_at
    ? new Date(value.set_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null

  if (value.provisional) {
    return value.source === 'signup'
      ? 'From signup, unconfirmed'
      : 'Unconfirmed'
  }

  switch (value.source) {
    case 'signup':
      return when ? `From signup, confirmed ${when}` : 'From signup, confirmed'
    case 'vera_default':
      return 'Vera default'
    case 'user':
    default:
      return when ? `Set in Vera on ${when}` : 'Set in Vera'
  }
}

/**
 * A catalogue entry's spec, falling back to the shared table.
 *
 * The API's catalogue entries already carry every `IMetricInputSpec` field, so this
 * is a pure convenience for the two places that hold only a key. It reads from
 * `METRIC_INPUT_SPECS` rather than re-declaring bounds — the whole reason that
 * table exists in `@repo/types` is that it is the one place a unit is written down.
 */
export const specForKey = (key: MetricInputKey) => METRIC_INPUT_SPECS[key]
